// feature-module/usermanagement/UserList.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw,
    Eye, Edit, Trash2, ChevronUp,
    User, Mail, Phone, Shield, Lock, Unlock,

} from 'feather-icons-react/build/IconComponents';
import { userActions } from "../../core/action/userAction";
import UserModal from "../../core/modals/usermanagement/userModel";
import RoleModal from "../../core/modals/usermanagement/roleModel";

const MySwal = withReactContent(Swal);

const UserList = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // State management
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [activeTab, setActiveTab] = useState('users'); // 'users' or 'roles'

    // Modal states
    const [showUserModal, setShowUserModal] = useState(false);
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedRole, setSelectedRole] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        role_id: '',
        status: ''
    });

    // Sort options
    const sortOptions = [
        { value: 'created_desc', label: 'Newest First' },
        { value: 'created_asc', label: 'Oldest First' },
        { value: 'name_asc', label: 'Name (A-Z)' },
        { value: 'name_desc', label: 'Name (Z-A)' },
        { value: 'email_asc', label: 'Email (A-Z)' },
        { value: 'email_desc', label: 'Email (Z-A)' }
    ];

    const [sortOption, setSortOption] = useState('created_desc');

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0,
        totalRoles: 0
    });

    // Check permissions
    const canCreate = userActions.hasPermission('create-users');
    const canEdit = userActions.hasPermission('edit-users');
    const canDelete = userActions.hasPermission('delete-users');
    const canView = userActions.hasPermission('view-users');
    const canManageRoles = userActions.hasPermission('view-roles');
    const isSuperAdmin = userActions.isSuperAdmin();

    // Load roles on component mount - THIS IS THE FIX
    useEffect(() => {
        loadRoles();
    }, []); // Empty dependency array means this runs once on mount

    // Load users when active tab changes to users
    useEffect(() => {
        if (activeTab === 'users') {
            loadUsers();
        }
    }, [activeTab]);

    // Update statistics when users change
    useEffect(() => {
        const active = users.filter(u => u.status === 'active').length;
        const inactive = users.filter(u => u.status === 'inactive').length;

        setStats(prev => ({
            ...prev,
            total: users.length,
            active,
            inactive
        }));
    }, [users]);

    // Load users from API
    const loadUsers = async (page = pagination.page) => {
        if (!canView) {
            toast.error('You do not have permission to view users');
            return;
        }

        try {
            setLoading(true);

            const [sortBy, sortOrder] = sortOption.split('_');

            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                role_id: filters.role_id,
                status: filters.status,
                sort_by: sortBy === 'created' ? 'created_at' : sortBy,
                sort_order: sortOrder === 'desc' ? 'DESC' : 'ASC'
            };

            const result = await userActions.getUsers(params);

            if (result.success) {
                setUsers(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                toast.error(result.message || 'Failed to load users');
            }
        } catch (error) {
            console.error('Error loading users:', error);
            toast.error('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    // Load roles from API - NOW CALLED ON MOUNT
    const loadRoles = async () => {
        try {
            // Don't show loading for roles if already loading
            const result = await userActions.getRoles();

            if (result.success) {
                setRoles(result.data || []);
                setStats(prev => ({ ...prev, totalRoles: result.data.length }));
            } else {
                console.warn('Failed to load roles:', result.message);
            }
        } catch (error) {
            console.error('Error loading roles:', error);
        }
    };

    // Handle tab change
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        if (tab === 'roles') {
            // Refresh roles when switching to roles tab
            loadRoles();
        }
    };

    // Handle add user
    const handleAddUser = () => {
        if (!canCreate) {
            toast.error('You do not have permission to create users');
            return;
        }
        setModalMode("add");
        setSelectedUser(null);
        setShowUserModal(true);
    };

    // Handle edit user
    const handleEditUser = (user) => {
        if (!canEdit) {
            toast.error('You do not have permission to edit users');
            return;
        }
        setModalMode("edit");
        setSelectedUser(user);
        setShowUserModal(true);
    };

    // Handle view user
    const handleViewUser = (user) => {
        setModalMode("view");
        setSelectedUser(user);
        setShowUserModal(true);
    };

    // Handle delete user
    const handleDeleteUser = async (id, name) => {
        if (!canDelete) {
            toast.error('You do not have permission to delete users');
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete User?',
            html: `<p>Are you sure you want to delete <strong>${name}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await userActions.deleteUser(id);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'User has been deleted.',
                        timer: 2000
                    });
                    loadUsers(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete user',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting user:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete user',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle add role
    const handleAddRole = () => {
        setModalMode("add");
        setSelectedRole(null);
        setShowRoleModal(true);
    };

    // Handle edit role
    const handleEditRole = (role) => {
        setModalMode("edit");
        setSelectedRole(role);
        setShowRoleModal(true);
    };

    // Handle delete role
    const handleDeleteRole = async (id, name, isSystem) => {
        if (isSystem == 1) {
            MySwal.fire({
                icon: 'error',
                title: 'Cannot Delete',
                text: 'System roles cannot be deleted',
                timer: 3000
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete Role?',
            html: `<p>Are you sure you want to delete role <strong>${name}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await userActions.deleteRole(id);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Role has been deleted.',
                        timer: 2000
                    });
                    loadRoles();
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete role',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting role:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete role',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle modal close
    const handleUserModalClose = (refresh = false) => {
        setShowUserModal(false);
        setSelectedUser(null);
        if (refresh) {
            loadUsers(pagination.page);
        }
    };

    const handleRoleModalClose = (refresh = false) => {
        setShowRoleModal(false);
        setSelectedRole(null);
        if (refresh) {
            loadRoles();
        }
    };

    // Get user initials
    const getInitials = (name) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Get random color for avatar
    const getAvatarColor = (id) => {
        const colors = [
            'bg-primary',
            'bg-success',
            'bg-info',
            'bg-warning',
            'bg-danger',
            'bg-secondary'
        ];
        return colors[(id % colors.length)];
    };

    // Format date
    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    // Get role badge color
    const getRoleBadge = (roleSlug) => {
        switch (roleSlug) {
            case 'super-admin':
                return 'bg-danger';
            case 'admin':
                return 'bg-warning';
            case 'manager':
                return 'bg-info';
            default:
                return 'bg-secondary';
        }
    };

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>User Management</h4>
                        <h6>Manage users, roles and permissions</h6>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => activeTab === 'users' ? loadUsers() : loadRoles()}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                    </ul>

                    {activeTab === 'users' && canCreate && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={handleAddUser}>
                                <Plus size={18} className="me-2" />
                                Add User
                            </button>
                        </div>
                    )}

                    {activeTab === 'roles' && isSuperAdmin && (
                        <div className="page-btn">
                            <button className="btn btn-primary" onClick={handleAddRole}>
                                <Plus size={18} className="me-2" />
                                Add Role
                            </button>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <ul className="nav nav-tabs mb-4">
                    <li className="nav-item">
                        <button
                            className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                            onClick={() => handleTabChange('users')}
                        >
                            <User size={16} className="me-2" />
                            Users ({stats.total})
                        </button>
                    </li>
                    {canManageRoles && (
                        <li className="nav-item">
                            <button
                                className={`nav-link ${activeTab === 'roles' ? 'active' : ''}`}
                                onClick={() => handleTabChange('roles')}
                            >
                                <Shield size={16} className="me-2" />
                                Roles & Permissions ({stats.totalRoles})
                            </button>
                        </li>
                    )}
                </ul>

                {/* Statistics Cards */}
                {activeTab === 'users' && (
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                            <User size={24} className="text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Total Users</p>
                                            <h3 className="mb-0">{stats.total}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                            <Unlock size={24} className="text-success" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Active</p>
                                            <h3 className="mb-0">{stats.active}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                                            <Lock size={24} className="text-danger" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Inactive</p>
                                            <h3 className="mb-0">{stats.inactive}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters - Users */}
                {activeTab === 'users' && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-4">
                                    <div className="mb-3">
                                        <label>Search</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Name, email, phone..."
                                                value={filters.search}
                                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                                onKeyPress={(e) => e.key === 'Enter' && loadUsers(1)}
                                            />
                                            <button className="btn btn-primary" onClick={() => loadUsers(1)}>
                                                <Search size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-3">
                                    <div className="mb-3">
                                        <label>Role</label>
                                        <select
                                            className="form-select"
                                            value={filters.role_id}
                                            onChange={(e) => {
                                                setFilters({ ...filters, role_id: e.target.value });
                                                loadUsers(1);
                                            }}
                                        >
                                            <option value="">All Roles</option>
                                            {roles.map(role => (
                                                <option key={role.id} value={role.id}>{role.role_name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-3">
                                    <div className="mb-3">
                                        <label>Status</label>
                                        <select
                                            className="form-select"
                                            value={filters.status}
                                            onChange={(e) => {
                                                setFilters({ ...filters, status: e.target.value });
                                                loadUsers(1);
                                            }}
                                        >
                                            <option value="">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="mb-3">
                                        <label>Sort By</label>
                                        <Select
                                            options={sortOptions}
                                            value={sortOptions.find(s => s.value === sortOption)}
                                            onChange={(opt) => {
                                                setSortOption(opt?.value || 'created_desc');
                                                loadUsers(1);
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Users Table */}
                {activeTab === 'users' && (
                    <div className="card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Quick search..."
                                            className="form-control"
                                            value={filters.search}
                                            onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                            onKeyPress={(e) => e.key === 'Enter' && loadUsers(1)}
                                        />
                                        <button className="btn btn-searchset">
                                            <Search size={16} />
                                        </button>
                                    </div>
                                </div>
                                <div className="wordset">
                                    <span className="badge bg-primary">{users.length} entries</span>
                                </div>
                            </div>

                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : users.length === 0 ? (
                                <div className="text-center py-5">
                                    <User size={48} className="text-muted mb-3" />
                                    <h5>No users found</h5>
                                    <p className="text-muted">Add your first user to get started</p>
                                    {canCreate && (
                                        <button className="btn btn-primary" onClick={handleAddUser}>
                                            <Plus size={16} className="me-2" />
                                            Add User
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th width="40px">
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={selectedRows.length === users.length}
                                                        onChange={(e) => {
                                                            if (e.target.checked) {
                                                                setSelectedRows(users.map(u => u.id));
                                                            } else {
                                                                setSelectedRows([]);
                                                            }
                                                        }}
                                                    />
                                                </th>
                                                <th>Employee ID</th>
                                                <th>User</th>
                                                <th>Contact</th>
                                                <th>Role</th>
                                                <th>Department</th>
                                                <th>Status</th>
                                                <th>Joined</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users.map((user) => (
                                                <tr key={user.id}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectedRows.includes(user.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedRows([...selectedRows, user.id]);
                                                                } else {
                                                                    setSelectedRows(selectedRows.filter(id => id !== user.id));
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-light text-dark">
                                                            {user.employee_id || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className={`avatar avatar-sm me-2 ${getAvatarColor(user.id)} rounded-circle d-flex align-items-center justify-content-center text-white`}>
                                                                {getInitials(user.name)}
                                                            </div>
                                                            <div>
                                                                <strong>{user.name}</strong>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            {user.email && (
                                                                <div className="small">
                                                                    <Mail size={12} className="me-1 text-muted" />
                                                                    {user.email}
                                                                </div>
                                                            )}
                                                            {user.phone && (
                                                                <div className="small">
                                                                    <Phone size={12} className="me-1 text-muted" />
                                                                    {user.phone}
                                                                </div>
                                                            )}
                                                            {!user.email && !user.phone && (
                                                                <span className="text-muted">No contact</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${getRoleBadge(user.role_slug)}`}>
                                                            {user.role_name || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {user.department || (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${user.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                            {user.status || 'inactive'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {formatDate(user.created_at)}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-outline-info me-1"
                                                            onClick={() => handleViewUser(user)}
                                                            title="View Details"
                                                        >
                                                            <Eye size={14} />
                                                        </button>
                                                        {canEdit && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => handleEditUser(user)}
                                                                title="Edit"
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                        )}
                                                        {canDelete && user.role_slug !== 'super-admin' && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteUser(user.id, user.name)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Pagination */}
                            {pagination.totalPages > 1 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div className="text-muted">
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                        {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                    </div>
                                    <nav>
                                        <ul className="pagination">
                                            <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => loadUsers(pagination.page - 1)}>
                                                    Previous
                                                </button>
                                            </li>
                                            {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                let pageNum = pagination.page;
                                                if (pagination.totalPages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (pagination.page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (pagination.page >= pagination.totalPages - 2) {
                                                    pageNum = pagination.totalPages - 4 + i;
                                                } else {
                                                    pageNum = pagination.page - 2 + i;
                                                }
                                                return (
                                                    <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                        <button className="page-link" onClick={() => loadUsers(pageNum)}>
                                                            {pageNum}
                                                        </button>
                                                    </li>
                                                );
                                            })}
                                            <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                <button className="page-link" onClick={() => loadUsers(pagination.page + 1)}>
                                                    Next
                                                </button>
                                            </li>
                                        </ul>
                                    </nav>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Roles Table */}
                {activeTab === 'roles' && (
                    <div className="card">
                        <div className="card-body">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : roles.length === 0 ? (
                                <div className="text-center py-5">
                                    <Shield size={48} className="text-muted mb-3" />
                                    <h5>No roles found</h5>
                                    <p className="text-muted">Create your first role to get started</p>
                                    {isSuperAdmin && (
                                        <button className="btn btn-primary" onClick={handleAddRole}>
                                            <Plus size={16} className="me-2" />
                                            Add Role
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                <th>Role Name</th>
                                                <th>Slug</th>
                                                <th>Description</th>
                                                <th>Users Count</th>
                                                <th>Status</th>
                                                <th>Type</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {roles.map((role) => (
                                                <tr key={role.id}>
                                                    <td>
                                                        <strong>{role.role_name}</strong>
                                                    </td>
                                                    <td>
                                                        <code>{role.role_slug}</code>
                                                    </td>
                                                    <td>
                                                        {role.description || '-'}
                                                    </td>
                                                    <td>
                                                        <span className="badge bg-info">
                                                            {role.user_count || 0} users
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${role.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                            {role.status}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        {role.is_system == 1 ? (
                                                            <span className="badge bg-secondary">System</span>
                                                        ) : (
                                                            <span className="badge bg-primary">Custom</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            className="btn btn-sm btn-outline-primary me-1"
                                                            onClick={() => handleEditRole(role)}
                                                            title="Edit Role & Permissions"
                                                        >
                                                            <Shield size={14} className="me-1" />
                                                            Permissions
                                                        </button>
                                                        {role.is_system != 1 && isSuperAdmin && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteRole(role.id, role.role_name, role.is_system)}
                                                                title="Delete Role"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* User Modal */}
            {showUserModal && (
                <UserModal
                    mode={modalMode}
                    user={selectedUser}
                    roles={roles}
                    onClose={handleUserModalClose}
                />
            )}

            {/* Role Modal */}
            {showRoleModal && (
                <RoleModal
                    mode={modalMode}
                    role={selectedRole}
                    onClose={handleRoleModalClose}
                />
            )}
        </div>
    );
};

export default UserList;